import React, { useContext, useEffect, useId, useRef, useState } from 'react';
import { FaCrown, FaTimes, FaCheck, FaRocket } from 'react-icons/fa';
import { ThemeContext } from '../App';
import { MercadoPagoService } from '../services/MercadoPagoService';
import { PAYMENT_CATALOG, formatCurrency, getAnnualSavings, getUpgradeOptions } from '../config/paymentCatalog';

/**
 * Modal de Upgrade exibido quando usuário atinge limite do plano
 * 
 * @param {Object} props
 * @param {boolean} props.show - Se o modal deve ser exibido
 * @param {Function} props.onClose - Callback ao fechar
 * @param {string} props.recurso - Recurso bloqueado (propriedades, alertas, etc)
 * @param {string} props.planoAtual - Plano atual do usuário
 * @param {Object} props.limiteInfo - Informações sobre o limite atingido
 * @param {Object} props.usuario - Dados do usuário logado
 * 
 * @example
 * <UpgradeModal 
 *   show={showModal}
 *   onClose={() => setShowModal(false)}
 *   recurso="propriedades"
 *   planoAtual="starter"
 *   limiteInfo={{ usado: 1, limite: 1 }}
 *   usuario={usuario}
 * />
 */
export default function UpgradeModal({ show, onClose, recurso, planoAtual = 'starter', limiteInfo = {}, usuario }) {
  const { darkMode } = useContext(ThemeContext);
  const [processando, setProcessando] = useState(false);
  const [periodicidade, setPeriodicidade] = useState({});
  const [mpInicializado, setMpInicializado] = useState(false);
  const [planoProcessando, setPlanoProcessando] = useState(null);
  const [erroPagamento, setErroPagamento] = useState('');
  const modalRef = useRef(null);
  const tituloId = useId();
  const descricaoId = useId();
  
  // Inicializa o Mercado Pago quando o modal é exibido
  useEffect(() => {
    if (show && !mpInicializado) {
      MercadoPagoService.inicializar()
        .then(() => {
          setMpInicializado(true);
        })
        .catch((error) => {
          console.error('Erro ao inicializar Mercado Pago:', error);
          setErroPagamento(error.message || 'Não foi possível carregar o pagamento. Tente novamente.');
        });
    }
  }, [show, mpInicializado]);
  
  // Detecta se é mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Previne scroll do body quando modal está aberto (especialmente em mobile)
  useEffect(() => {
    if (show && isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [show, isMobile]);

  useEffect(() => {
    if (!show) return undefined;
    const previouslyFocused = document.activeElement;
    const focusTimer = window.setTimeout(() => {
      modalRef.current?.querySelector('button:not([disabled])')?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !modalRef.current) return;
      const focusable = Array.from(modalRef.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(element => !element.hasAttribute('hidden'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [show, onClose]);

  if (!show) return null;

  // Mensagens personalizadas por recurso
  const mensagens = {
    propriedades: {
      titulo: '🏡 Limite de Propriedades Atingido',
      descricao: 'Você atingiu o limite de propriedades do seu plano.',
      icone: '🏡'
    },
    talhoes: {
      titulo: '📐 Limite de Talhões Atingido',
      descricao: 'Você atingiu o limite de talhões do seu plano.',
      icone: '📐'
    },
    alertas: {
      titulo: '🔔 Limite de Alertas Atingido',
      descricao: 'Você atingiu o limite de alertas climáticos do seu plano.',
      icone: '🔔'
    },
    sensores: {
      titulo: '📡 Limite de Sensores Atingido',
      descricao: 'Você atingiu o limite de sensores do seu plano.',
      icone: '📡'
    },
    compartilhar: {
      titulo: '📤 Limite de Compartilhamentos Atingido',
      descricao: 'Você atingiu o limite de compartilhamentos do seu plano. Faça upgrade para compartilhar com mais pessoas.',
      icone: '📤'
    },
    relatorios: {
      titulo: '📄 Relatórios Indisponíveis',
      descricao: 'A exportação de relatórios é uma feature Premium!',
      icone: '📄'
    },
    download: {
      titulo: '💾 Download Indisponível',
      descricao: 'O download de dados é uma feature Premium!',
      icone: '💾'
    },
    upgrade: {
      titulo: 'Compare os planos',
      descricao: 'Escolha um nível acima do seu plano atual e revise a recorrência antes de assinar.',
      icone: '↗'
    }
  };

  const msg = mensagens[recurso] || {
    titulo: '🔒 Recurso Bloqueado',
    descricao: 'Este recurso não está disponível no seu plano atual.',
    icone: '🔒'
  };

  // Os planos comercializáveis vêm do mesmo catálogo usado pelo checkout.
  const opcoesUpgrade = getUpgradeOptions(planoAtual);
  const primeiraOpcaoPaga = opcoesUpgrade.find(plan => plan.saleable)?.id;
  const planos = opcoesUpgrade.map(plan => ({
    id: plan.id,
    nome: plan.name,
    preco: plan.monthly,
    precoAnual: plan.annual,
    badge: plan.id === 'essential' ? '🌱' : plan.id === 'professional' ? '🚀' : plan.id === 'management' ? '🛡️' : '👑',
    cor: plan.color,
    features: plan.features,
    recomendado: plan.id === primeiraOpcaoPaga,
  }));

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: isMobile ? 'flex-end' : 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: isMobile ? 0 : 20,
    animation: 'fadeIn 0.2s ease-out',
    overflowY: 'auto'
  };

  const modalStyle = {
    background: darkMode ? '#1e1e1e' : '#fff',
    borderRadius: isMobile ? '20px 20px 0 0' : 20,
    maxWidth: isMobile ? '100%' : 900,
    width: '100%',
    maxHeight: isMobile ? '95vh' : '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 50px rgba(0, 0, 0, 0.3)',
    animation: isMobile ? 'slideUpMobile 0.3s ease-out' : 'slideUp 0.3s ease-out',
    color: darkMode ? '#f8fafc' : '#1e293b',
    margin: isMobile ? 0 : 'auto'
  };

  const headerStyle = {
    background: `linear-gradient(135deg, ${darkMode ? '#667eea' : '#1976d2'} 0%, ${darkMode ? '#764ba2' : '#0d47a1'} 100%)`,
    color: '#fff',
    padding: isMobile ? '20px 16px' : '24px 32px',
    borderRadius: isMobile ? '20px 20px 0 0' : '20px 20px 0 0',
    position: 'relative'
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: isMobile ? 12 : 20,
    right: isMobile ? 12 : 20,
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '50%',
    width: isMobile ? 32 : 36,
    height: isMobile ? 32 : 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#fff',
    transition: 'background 0.2s',
    fontSize: isMobile ? 16 : 18,
    zIndex: 10
  };

  const planCardStyle = (plano) => ({
    background: darkMode ? '#2a2a2a' : '#f8fafc',
    borderRadius: isMobile ? 12 : 16,
    padding: isMobile ? 16 : 24,
    border: plano.recomendado ? `${isMobile ? 2 : 3}px solid ${plano.cor}` : `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
    position: 'relative',
    transition: 'transform 0.2s, box-shadow 0.2s',
    height: '100%'
  });

  return (
    <div
      style={overlayStyle}
      onClick={(event) => event.target === event.currentTarget && onClose()}
      role="presentation"
    >
      <div
        ref={modalRef}
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        aria-describedby={descricaoId}
      >
        {/* Header */}
        <div style={headerStyle}>
          <button 
            type="button"
            style={closeButtonStyle}
            onClick={onClose}
            aria-label="Fechar comparação de planos"
            onMouseEnter={!isMobile ? (e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)' : undefined}
            onMouseLeave={!isMobile ? (e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)' : undefined}
          >
            <FaTimes />
          </button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? 36 : 48, marginBottom: isMobile ? 8 : 12 }}>{msg.icone}</div>
            <h2 id={tituloId} style={{ margin: 0, fontSize: isMobile ? 20 : 28, fontWeight: 900, lineHeight: 1.3 }}>{msg.titulo}</h2>
            <p id={descricaoId} style={{ margin: '8px 0 0 0', fontSize: isMobile ? 14 : 16, opacity: 0.95, lineHeight: 1.4 }}>{msg.descricao}</p>
            {limiteInfo.limite && (
              <div style={{ 
                marginTop: isMobile ? 12 : 16, 
                background: 'rgba(255, 255, 255, 0.2)', 
                padding: isMobile ? '6px 12px' : '8px 16px', 
                borderRadius: 8,
                display: 'inline-block',
                fontSize: isMobile ? 12 : 14,
                fontWeight: 600
              }}>
                Você está usando {limiteInfo.usado} de {limiteInfo.limite}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? '20px 16px' : 32 }}>
          <div role={erroPagamento ? 'alert' : 'status'} aria-live="polite" aria-atomic="true" style={{ minHeight: erroPagamento ? 'auto' : 0 }}>
            {erroPagamento && (
              <div style={{
                background: darkMode ? '#4c1d1d' : '#fff1f2',
                color: darkMode ? '#fecaca' : '#9f1239',
                border: `1px solid ${darkMode ? '#b91c1c' : '#fecdd3'}`,
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 18,
                lineHeight: 1.45,
              }}>
                {erroPagamento}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 32 }}>
            <FaCrown size={isMobile ? 32 : 40} color="#FFD700" style={{ marginBottom: isMobile ? 8 : 12 }} />
            <h3 style={{ margin: 0, fontSize: isMobile ? 18 : 24, fontWeight: 700, color: darkMode ? '#ffd54f' : '#1976d2', lineHeight: 1.3 }}>
              Desbloqueie Todo o Potencial do ClimoraX
            </h3>
            <p style={{ margin: '8px 0 0 0', fontSize: isMobile ? 14 : 16, opacity: 0.8 }}>
              Escolha o plano ideal para suas necessidades
            </p>
          </div>

          {/* Planos */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : planos.length === 3 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
            gap: isMobile ? 16 : 24,
            marginBottom: isMobile ? 16 : 24
          }}>
            {planos.map((plano) => (
              <article
                key={plano.id}
                style={planCardStyle(plano)}
                aria-labelledby={`plano-${plano.id}-titulo`}
                onMouseEnter={!isMobile ? (e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 8px 24px ${plano.cor}33`;
                } : undefined}
                onMouseLeave={!isMobile ? (e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                } : undefined}
              >
                {plano.recomendado && (
                  <div style={{
                    position: 'absolute',
                    top: isMobile ? -10 : -12,
                    right: isMobile ? 16 : 24,
                    background: plano.cor,
                    color: '#fff',
                    padding: isMobile ? '3px 12px' : '4px 16px',
                    borderRadius: isMobile ? 8 : 12,
                    fontSize: isMobile ? 10 : 12,
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }}>
                    RECOMENDADO
                  </div>
                )}

                <div style={{ textAlign: 'center', marginBottom: isMobile ? 16 : 20 }}>
                  <div style={{ fontSize: isMobile ? 24 : 32, marginBottom: isMobile ? 6 : 8 }}>{plano.badge}</div>
                  <h4 id={`plano-${plano.id}-titulo`} style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 700, color: plano.cor }}>
                    {plano.nome}
                  </h4>
                  <div style={{ marginTop: isMobile ? 8 : 12 }}>
                    <div style={{ fontSize: isMobile ? 12 : 14, opacity: 0.7 }}>A partir de</div>
                    <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 900, color: darkMode ? '#ffd54f' : '#1976d2' }}>
                      {formatCurrency(plano.preco)}
                      <span style={{ fontSize: isMobile ? 12 : 14, fontWeight: 400, opacity: 0.7 }}>/mês</span>
                    </div>
                    <div style={{ fontSize: isMobile ? 12 : 14, opacity: 0.8, marginTop: 4 }}>
                      ou {formatCurrency(plano.precoAnual)}/ano
                    </div>
                    <div style={{
                      fontSize: isMobile ? 11 : 12,
                      color: '#4caf50',
                      fontWeight: 600,
                      marginTop: 4
                    }}>
                      Economize {formatCurrency(getAnnualSavings(plano.id))} no acesso anual
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div style={{ marginTop: isMobile ? 16 : 20 }}>
                  {plano.features.map((feature, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: isMobile ? 8 : 12,
                      marginBottom: isMobile ? 10 : 12,
                      fontSize: isMobile ? 13 : 14
                    }}>
                      <FaCheck style={{ color: '#4caf50', flexShrink: 0, fontSize: isMobile ? 12 : 14 }} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Seleção de Periodicidade */}
                <div style={{
                    marginTop: isMobile ? 16 : 20,
                    display: 'flex',
                    gap: 8,
                    justifyContent: 'center'
                  }}>
                    <button
                      type="button"
                      onClick={() => setPeriodicidade({ ...periodicidade, [plano.id]: 'mensal' })}
                      aria-pressed={!periodicidade[plano.id] || periodicidade[plano.id] === 'mensal'}
                      aria-label={`Cobrança mensal do plano ${plano.nome}: ${formatCurrency(plano.preco)} por mês`}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        background: (!periodicidade[plano.id] || periodicidade[plano.id] === 'mensal') 
                          ? (darkMode ? '#444' : '#e2e8f0')
                          : 'transparent',
                        border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
                        borderRadius: 8,
                        fontSize: isMobile ? 12 : 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: darkMode ? '#fff' : '#1e293b'
                      }}
                    >
                      Mensal
                    </button>
                    <button
                      type="button"
                      onClick={() => setPeriodicidade({ ...periodicidade, [plano.id]: 'anual' })}
                      aria-pressed={periodicidade[plano.id] === 'anual'}
                      aria-label={`Cobrança anual do plano ${plano.nome}: ${formatCurrency(plano.precoAnual)} por ano`}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        background: periodicidade[plano.id] === 'anual'
                          ? (darkMode ? '#444' : '#e2e8f0')
                          : 'transparent',
                        border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
                        borderRadius: 8,
                        fontSize: isMobile ? 12 : 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: darkMode ? '#fff' : '#1e293b'
                      }}
                    >
                      Anual 💰
                    </button>
                </div>

                {/* Botão */}
                <button
                  type="button"
                  disabled={processando}
                  aria-busy={planoProcessando === plano.id}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 20px' : '14px 24px',
                    marginTop: isMobile ? 12 : 16,
                    background: plano.recomendado 
                      ? `linear-gradient(135deg, ${plano.cor} 0%, ${plano.cor}dd 100%)`
                      : darkMode ? '#444' : '#e2e8f0',
                    color: plano.recomendado ? '#fff' : (darkMode ? '#fff' : '#1e293b'),
                    border: 'none',
                    borderRadius: isMobile ? 10 : 12,
                    fontSize: isMobile ? 14 : 16,
                    fontWeight: 700,
                    cursor: processando ? 'not-allowed' : 'pointer',
                    transition: 'transform 0.2s, opacity 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isMobile ? 6 : 8,
                    opacity: processando ? 0.6 : 1
                  }}
                  onMouseEnter={!isMobile && !processando ? (e) => {
                    e.target.style.transform = 'scale(1.02)';
                    e.target.style.opacity = processando ? '0.6' : '0.9';
                  } : undefined}
                  onMouseLeave={!isMobile && !processando ? (e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.opacity = processando ? '0.6' : '1';
                  } : undefined}
                  onClick={async () => {
                    if (!usuario) {
                      setErroPagamento('Sua sessão não foi identificada. Entre novamente para assinar.');
                      return;
                    }

                    setProcessando(true);
                    setPlanoProcessando(plano.id);
                    setErroPagamento('');
                    
                    try {
                      const period = periodicidade[plano.id] || 'mensal';
                      
                      await MercadoPagoService.abrirCheckout({
                        plano: plano.id,
                        periodicidade: period,
                        usuario: {
                          id: usuario.id,
                          nome: usuario.nome || usuario.email?.split('@')[0] || 'Usuário',
                          email: usuario.email
                        }
                      });
                    } catch (error) {
                      console.error('Erro ao processar pagamento:', error);
                      setErroPagamento(error.message || 'Não foi possível iniciar a assinatura. Tente novamente.');
                      setProcessando(false);
                      setPlanoProcessando(null);
                    }
                  }}
                >
                  {planoProcessando === plano.id ? 'Preparando checkout seguro...' : (
                    <>
                      {plano.recomendado ? <FaRocket /> : null}
                      {`Assinar ${plano.nome}`}
                    </>
                  )}
                </button>
              </article>
            ))}
          </div>

          {/* Footer */}
          <div style={{ 
            textAlign: 'center', 
            paddingTop: isMobile ? 16 : 24, 
            borderTop: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
            fontSize: isMobile ? 12 : 14,
            opacity: 0.8
          }}>
            <p style={{ margin: 0, lineHeight: 1.5 }}>
              A cobrança é renovada automaticamente no cartão.
            </p>
            <p style={{ margin: '8px 0 0 0', lineHeight: 1.5 }}>
              Você pode cancelar cobranças futuras quando quiser.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          [role="dialog"] { animation: none !important; scroll-behavior: auto !important; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideUpMobile {
          from { 
            opacity: 0;
            transform: translateY(100%);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

